import { currentHost, resolveSiteLink } from "@/lib/site/hosts";

/** The two languages every public string exists in, Czech first. */
export const LANDING_LOCALES = ["cs", "en"] as const;

export type LandingLocale = (typeof LANDING_LOCALES)[number];

/** One string in both languages. */
export type Localized = Record<LandingLocale, string>;

export function isLandingLocale(value: string | undefined): value is LandingLocale {
  return (LANDING_LOCALES as readonly string[]).includes(value ?? "");
}

/**
 * A picker for one locale: `const tr = makeTr(lang); tr("Ceník", "Pricing")`.
 * Keeps the Czech and English of a string side by side in the component.
 */
export function makeTr(locale: LandingLocale) {
  return (cs: string, en: string): string => (locale === "cs" ? czechNbsp(cs) : en);
}

/** The string of a `Localized` record for one locale. */
export function pick(value: Localized, locale: LandingLocale): string {
  return locale === "cs" ? czechNbsp(value.cs) : value[locale];
}

const ONE_LETTER_WORD = /(^|[\s(„])([ksvzouaiKSVZOUAI]) /g;

/**
 * Czech typography: a one-letter word (k, s, v, z, o, u, a, i) never ends a
 * line, so the space after it becomes a no-break space.
 */
export function czechNbsp(text: string): string {
  // Twice, for neighbours like "a v Excelu" where the matches overlap.
  return text.replace(ONE_LETTER_WORD, "$1$2\u00a0").replace(ONE_LETTER_WORD, "$1$2\u00a0");
}

/**
 * A link in the page's language, independent of the language context and
 * written for the host serving the page: public pages get their slug
 * ("/privacy" → `/ochrana-osobnich-udaju`), app paths go to app.zettl.cz
 * from zettl.cz and stay relative
 * everywhere else. See `@/lib/site/hosts`.
 */
export function localePath(locale: LandingLocale, path: string): string {
  return resolveSiteLink(path || "/", locale, currentHost());
}
